/**
 * Date Getter Utilities
 *
 * This file contains utility functions for extracting information and components from dates.
 * These functions READ properties and boundaries from dates without modifying them.
 * Use these utilities when you need to:
 * - Extract date components as numbers (e.g., get year → 2024, get month → 1, get weekday → 3)
 * - Get the current moment (e.g., today's date, current timestamp, now)
 * - Find period boundaries relative to a date (e.g., start of week, end of month, beginning of quarter)
 * - Access timezone information (e.g., UTC offset, timezone name)
 * - Retrieve weekday or month names in different locales (e.g., "Monday" in French → "lundi")
 * - Identify which period a date belongs to (e.g., what week, what quarter)
 *
 * Key distinction: These functions EXTRACT values from dates, not create new dates or change formats.
 */
import {
  DateTime as LuxonDateTime,
  type DateTimeUnit as LuxonDateTimeUnit,
  type WeekdayNumbers as LuxonWeekdayNumbers,
} from 'luxon'
import type { MergedSettingsData } from '@/models/settings.js'
import { formatDate } from './conversion.js'
import { subtractToDate } from './calculation.js'

/**
 * Weekday values as returned from the API settings for `first_day_of_week`.
 *
 * These are string constants like 'WEEKDAY_MONDAY', 'WEEKDAY_TUESDAY', etc.
 */
export type WeekDaySettingValues = MergedSettingsData['first_day_of_week']

/**
 * Weekday numbers in ISO 8601 format (1-7).
 *
 * Used by Luxon and ISO 8601 standard:
 * - 1 = Monday
 * - 2 = Tuesday
 * - 3 = Wednesday
 * - 4 = Thursday
 * - 5 = Friday
 * - 6 = Saturday
 * - 7 = Sunday
 */
export type WeekDayISONumbers = LuxonWeekdayNumbers

/**
 * Weekday numbers in JavaScript format (0-6).
 *
 * Automatically derived from ISO-8601 format by subtracting 1 from each value using tuple indexing.
 * Used by JavaScript's `Date.getDay()` and some legacy systems:
 * - 0 = Sunday (ISO 7)
 * - 1 = Monday (ISO 1)
 * - 2 = Tuesday (ISO 2)
 * - 3 = Wednesday (ISO 3)
 * - 4 = Thursday (ISO 4)
 * - 5 = Friday (ISO 5)
 * - 6 = Saturday (ISO 6)
 */
export type WeekDayJSNumbers = 0 | 1 | 2 | 3 | 4 | 5 | 6

/**
 * Mapping of API weekday values to ISO-8601 weekday numbers.
 *
 * Converts weekday constants from the API response (e.g., 'WEEKDAY_MONDAY')
 * to ISO-8601 weekday numbering system (Mon=1, Sun=7).
 *
 * @example
 * ```typescript
 * weekdaySettingsOptions['WEEKDAY_MONDAY'] // Returns: 1
 * weekdaySettingsOptions['WEEKDAY_SUNDAY'] // Returns: 7
 * ```
 */
export const weekdaySettingsOptions: Record<
  WeekDaySettingValues,
  WeekDayISONumbers
> = {
  WEEKDAY_MONDAY: 1,
  WEEKDAY_TUESDAY: 2,
  WEEKDAY_WEDNESDAY: 3,
  WEEKDAY_THURSDAY: 4,
  WEEKDAY_FRIDAY: 5,
  WEEKDAY_SATURDAY: 6,
  WEEKDAY_SUNDAY: 7,
}

export const Weekdays = {
  Monday: weekdaySettingsOptions.WEEKDAY_MONDAY,
  Tuesday: weekdaySettingsOptions.WEEKDAY_TUESDAY,
  Wednesday: weekdaySettingsOptions.WEEKDAY_WEDNESDAY,
  Thursday: weekdaySettingsOptions.WEEKDAY_THURSDAY,
  Friday: weekdaySettingsOptions.WEEKDAY_FRIDAY,
  Saturday: weekdaySettingsOptions.WEEKDAY_SATURDAY,
  Sunday: weekdaySettingsOptions.WEEKDAY_SUNDAY,
} as const

/**
 * Converts a JavaScript Date object or an ISO 8601 date string to a Luxon DateTime object.
 * Note: This function should not be used outside of the utils/date folder.
 *
 * @param {Date | string} value - The JavaScript Date object or date-time string in ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ).
 * @returns {DateTimeMaybeValid} - The Luxon DateTime object representation of the input date.
 */
export function getDateTime(value: Date | string) {
  return typeof value === 'string'
    ? LuxonDateTime.fromISO(value)
    : LuxonDateTime.fromJSDate(value)
}

/**
 * Returns Luxon DateTime object for today's date.
 * Note: This function should not be used outside of the utils/date folder.
 *
 * @returns {LuxonDateTime<true>} - The Luxon DateTime object representation today's date.
 */
export function getTodayDateTime() {
  return LuxonDateTime.now()
}

/**
 * Returns today's date as a JavaScript Date object.
 *
 * @returns {Date} - Today's date as a JavaScript Date object.
 */
export function getTodayJSDate() {
  return getTodayDateTime().toJSDate()
}

/**
 * Returns today's date in ISO 8601 date-time string (YYYY-MM-DDTHH:mm:ss.sssZ).
 *
 * @returns {string} - The ISO 8601 date-time string representation of today's date (YYYY-MM-DDTHH:mm:ss.sssZ).
 */
export function getTodayDateTimeISO() {
  return getTodayDateTime().toISO() ?? ''
}

/**
 * Returns today's date in ISO 8601 date string (YYYY-MM-DD).
 *
 * @returns {string} - The ISO 8601 date string representation of the current date (YYYY-MM-DD).
 */
export function getTodayDateTimeISODate() {
  return getTodayDateTime().toISODate() ?? ''
}

/**
 * Converts a JavaScript Date object or an ISO 8601 date string (YYYY-MM-DDTHH:mm:ss.sssZ) to a timestamp in milliseconds.
 *
 * @param {Date | string} value - The JavaScript Date object or date-time string in ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ).
 * @returns {number} - The timestamp in milliseconds.
 */
export function getDateTimeTimestamp(value: Date | string) {
  return getDateTime(value).toMillis()
}

/**
 * Returns the current timestamp in milliseconds (standard JavaScript timestamp).
 *
 * @returns {number} - The current timestamp in milliseconds.
 */
export function getCurrentTimestamp() {
  return getDateTimeTimestamp(getTodayDateTimeISO())
}

/**
 * Returns a Luxon DateTime set to Monday (weekday=1) of the current local week,
 * at 00:00 (startOf('day')).
 */
export function getFirstDayOfWeek(
  firstDayOfWeek: WeekDayISONumbers = weekdaySettingsOptions['WEEKDAY_MONDAY'],
) {
  const today = LuxonDateTime.local()
  const firstDayOfWeekNr = firstDayOfWeek

  /**
   * If today's weekday matches the configured first day of the week,
   * return today's date as we're already at the start of the week.
   */
  if (today.weekday === firstDayOfWeekNr) {
    return today.startOf('day')
  }

  /**
   * Calculates the number of days between the current day and the previous Monday.
   * Uses the modulo operator to ensure the result is always positive and between 0-6.
   * If today is Monday, returns 0. For Tuesday returns 1, Wednesday 2, etc.
   * @example
   * // If today is Wednesday and firstDayOfWeekNr is 1 (Monday)
   * // today.weekday = 3, firstDayOfWeekNr = 1
   * // (3 - 1 + 7) % 7 = 2 days since Monday
   */
  const daysToMonday = (today.weekday - firstDayOfWeekNr + 7) % 7
  return today.minus({ days: daysToMonday }).startOf('day')
}

/**
 * Returns a string representing the ISO date of Monday (weekday=1) of the current local week.
 *
 * @returns {string} - The ISO 8601 date string representation of the first day of the week (YYYY-MM-DDTHH:mm:ss.sssZ).
 */
export function getFirstDayOfWeekISO(
  firstDayOfWeek: WeekDayISONumbers = weekdaySettingsOptions['WEEKDAY_MONDAY'],
) {
  return getFirstDayOfWeek(firstDayOfWeek).toISO() ?? ''
}

/**
 * Returns the ISO string of Monday (weekday=1) of the current local week,
 * at 00:00 (startOf('day')).
 *
 * @returns {string} - The ISO 8601 date-time string representation of Monday of the current week (YYYY-MM-DDTHH:mm:ss.sssZ).
 */
export function getMondayOfCurrentWeek() {
  const today = LuxonDateTime.local()
  const weekday = today.weekday
  return (
    today
      .minus({ days: weekday - 1 })
      .startOf('day')
      .toISO() ?? ''
  )
}

/**
 * Gets the weekday number (1-7) for a given date, where 1 is Monday and 7 is Sunday.
 *
 * @param {Date | string} date - The date to get the weekday from.
 * @returns {number} - The weekday number (1-7).
 */
export function getWeekday(date: Date | string) {
  return getDateTime(date).weekday
}

/**
 * Gets the localized weekday name for a given weekday number or date.
 *
 * @param {WeekDayISONumbers | WeekDayJSNumbers | Date | string} weekdayOrDate - The weekday number (1-7), Date object, ISO string, or index (0-6).
 * @param {string} locale - The locale code (e.g., 'en-US', 'fr-FR').
 * @param {'long' | 'short' | 'narrow'} [format='long'] - The format of the weekday name.
 * @param {WeekDayISONumbers} [firstDayOfWeek] - Optional first day of week (ISO format 1-7). When provided, treats the first parameter as an index (0-6) relative to this day.
 * @returns {string} - The localized weekday name.
 *
 * @example
 * getWeekdayName(1, 'en-US') // 'Monday'
 * getWeekdayName(1, 'fr-FR') // 'lundi'
 * getWeekdayName(new Date('2024-07-01'), 'en-US', 'short') // 'Mon'
 * getWeekdayName(0, 'en-US', 'long', 1) // 'Monday' (index 0 with Monday as first day)
 * getWeekdayName(0, 'en-US', 'long', 7) // 'Sunday' (index 0 with Sunday as first day)
 */
export function getWeekdayName(
  weekdayOrDate: WeekDayISONumbers | Date | string | number,
  locale: string,
  format: 'long' | 'short' | 'narrow' = 'long',
  firstDayOfWeek?: WeekDayISONumbers,
) {
  let dateTime: LuxonDateTime

  if (typeof weekdayOrDate === 'number') {
    let weekdayNumber: WeekDayISONumbers

    if (firstDayOfWeek !== undefined) {
      // Treat weekdayOrDate as an index (0-6) and calculate ISO weekday number
      // Formula: rotate from firstDayOfWeek by index positions
      weekdayNumber = (((firstDayOfWeek + weekdayOrDate - 1) % 7) +
        1) as WeekDayISONumbers
    } else {
      // Treat weekdayOrDate as ISO weekday number (1-7)
      weekdayNumber = weekdayOrDate as WeekDayISONumbers
    }

    dateTime = LuxonDateTime.local().set({ weekday: weekdayNumber })
  } else {
    // If it's a Date or string, convert it
    dateTime = getDateTime(weekdayOrDate)
  }

  return dateTime.setLocale(locale).toLocaleString({ weekday: format })
}

/**
 * Gets the day of the month for a given date.
 *
 * @param {Date | string} date - The date to get the day from.
 * @returns {number} - The day of the month (1-31).
 */
export function getDayOfMonth(date: Date | string) {
  return getDateTime(date).day
}

/**
 * Gets the month number for a given date (1-12).
 *
 * @param {Date | string} date - The date to get the month from.
 * @returns {number} - The month number (1-12).
 */
export function getMonthNumber(date: Date | string) {
  return getDateTime(date).month
}

/**
 * Gets the year for a given date.
 *
 * @param {Date | string} date - The date to get the year from.
 * @returns {number} - The year.
 */
export function getYear(date: Date | string) {
  return getDateTime(date).year
}

/**
 * Gets the current year.
 *
 * @returns {number} - The current year.
 */
export function getCurrentYear() {
  return getTodayDateTime().year
}

/**
 * Gets the start of the year quarter of a given date.
 * @param value - The JS Date object or date-time string in ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @param format - The desired string format, for instance 'YYYY-MM-DD'
 * @returns The date part of the input string in the format (YYYY-MM-DDTHH:mm:ss.sssZ).
 */
export function getStartOfQuarter(value: Date | string, format?: string) {
  const date = getDateTime(value)
  const quarter = Math.floor((date.month - 1) / 3)
  const quarterDate = date
    .set({ month: quarter * 3 + 1, day: 1 })
    .startOf('day')

  return format ? quarterDate.toFormat(format) : (quarterDate.toISO() ?? '')
}

/**
 * Gets the end of the year quarter of a given date.
 * @param value - The JS Date object or date-time string in ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ)
 * @param format - The desired string format, for instance 'YYYY-MM-DD'
 * @returns The date part of the input string in the format (YYYY-MM-DDTHH:mm:ss.sssZ).
 */
export function getEndOfQuarter(value: Date | string, format?: string) {
  const date = getDateTime(value)
  const quarter = Math.floor((date.month - 1) / 3)
  const quarterDate = date.set({ month: quarter * 3 + 3 }).endOf('month')

  return format ? quarterDate.toFormat(format) : (quarterDate.toISO() ?? '')
}

/**
 * Calculates the date range for the last N days, including today.
 *
 * Returns a range where the end date is today and the start date is N days before today.
 * Both dates are formatted as ISO date strings.
 *
 * @param days - The number of days to go back from today
 * @returns An object with startDate and endDate as ISO date strings
 *
 * @example
 * ```typescript
 * // If today is 2024-10-28
 * getLastNDaysRange(7)
 * // Returns: { startDate: '2024-10-21T00:00:00.000+00:00', endDate: '2024-10-28T00:00:00.000+00:00' }
 *
 * getLastNDaysRange(30)
 * // Returns: { startDate: '2024-09-28T00:00:00.000+00:00', endDate: '2024-10-28T00:00:00.000+00:00' }
 * ```
 */
export function getLastNDaysRange(days: number) {
  const endDate = formatDate(getTodayDateTimeISO())
  const startDate = formatDate(subtractToDate(endDate, { days }))
  return { startDate, endDate }
}

/**
 * Gets the start and end dates for a specific quarter of a given year.
 *
 * @param quarter - The quarter number (1-4)
 * @param year - The year
 * @returns Object containing the start and end dates as ISO date strings (YYYY-MM-DD) for the quarter
 *
 * @example
 * ```typescript
 * getQuarterRange(2, 2025)
 * // Returns: { startDate: '2025-04-01', endDate: '2025-06-30' }
 * ```
 */
export function getQuarterRange(quarter: 1 | 2 | 3 | 4, year: number) {
  const quarterStartMonths = [1, 4, 7, 10] as const
  const quarterEndMonths = [3, 6, 9, 12] as const

  const startMonth = quarterStartMonths[quarter - 1]
  const endMonth = quarterEndMonths[quarter - 1]

  const startDate = getDateTime(
    `${year}-${String(startMonth).padStart(2, '0')}-01`,
  )
    .startOf('day')
    .toFormat('yyyy-MM-dd')

  const endDate = getDateTime(`${year}-${String(endMonth).padStart(2, '0')}-01`)
    .endOf('month')
    .toFormat('yyyy-MM-dd')

  return {
    startDate,
    endDate,
  }
}

/**
 * Gets the start of a period for a given date.
 * Using function overload for extra type-safety.
 *
 * @param {Date | string} date - The date to get the start of period from.
 * @param {LuxonDateTimeUnit} period - The period ('year', 'quarter', 'month', 'week', 'day', 'hour', 'minute').
 * @param {WeekDayISONumbers} [firstDayOfWeek] - Optional. The first day of the week (1-7, Monday-Sunday). Only used when period is 'week'.
 * @returns {Date | string} - The start of the period. Return type matches input type.
 */
export function getStartOfPeriod(
  date: string,
  period: LuxonDateTimeUnit,
  firstDayOfWeek?: WeekDayISONumbers,
): string
export function getStartOfPeriod(
  date: Date,
  period: LuxonDateTimeUnit,
  firstDayOfWeek?: WeekDayISONumbers,
): Date
export function getStartOfPeriod(
  date: Date | string,
  period: LuxonDateTimeUnit,
  firstDayOfWeek?: WeekDayISONumbers,
): Date | string {
  const paramDate = typeof date === 'string' ? date : date.toISOString()
  const dateTime = LuxonDateTime.fromISO(paramDate, { setZone: true })

  let start: Date
  if (period === 'week' && firstDayOfWeek !== undefined) {
    // Calculate start of week based on custom first day of week
    const currentWeekday = dateTime.weekday
    const daysToSubtract = (currentWeekday - firstDayOfWeek + 7) % 7
    start = dateTime.minus({ days: daysToSubtract }).startOf('day').toJSDate()
  } else {
    start = dateTime.startOf(period).toJSDate()
  }

  return typeof date === 'string' ? start.toISOString() : start
}

/**
 * Gets the end of a period for a given date.
 * Using function overload for extra type-safety.
 *
 * @param {Date | string} date - The date to get the end of period from.
 * @param {LuxonDateTimeUnit} period - The period ('year', 'quarter', 'month', 'week', 'day', 'hour', 'minute').
 * @param {WeekDayISONumbers} [firstDayOfWeek] - Optional. The first day of the week (1-7, Monday-Sunday). Only used when period is 'week'.
 * @returns {Date | string} - The end of the period. Return type matches input type.
 *
 * @example
 * ```typescript
 * getEndOfPeriod('2024-01-15T10:30:00.000Z', 'day')
 * // Returns: "2024-01-15T23:59:59.999Z"
 *
 * getEndOfPeriod(new Date('2024-01-15'), 'month')
 * // Returns: Date object representing 2024-01-31T23:59:59.999Z
 * ```
 */
export function getEndOfPeriod(
  date: string,
  period: LuxonDateTimeUnit,
  firstDayOfWeek?: WeekDayISONumbers,
): string
export function getEndOfPeriod(
  date: Date,
  period: LuxonDateTimeUnit,
  firstDayOfWeek?: WeekDayISONumbers,
): Date
export function getEndOfPeriod(
  date: Date | string,
  period: LuxonDateTimeUnit,
  firstDayOfWeek?: WeekDayISONumbers,
): Date | string {
  const paramDate = typeof date === 'string' ? date : date.toISOString()
  const dateTime = LuxonDateTime.fromISO(paramDate, { setZone: true })

  let end: Date
  if (period === 'week' && firstDayOfWeek !== undefined) {
    // Calculate end of week based on custom first day of week
    const currentWeekday = dateTime.weekday
    const daysToSubtract = (currentWeekday - firstDayOfWeek + 7) % 7
    const startOfWeek = dateTime.minus({ days: daysToSubtract }).startOf('day')
    end = startOfWeek.plus({ days: 6 }).endOf('day').toJSDate()
  } else {
    end = dateTime.endOf(period).toJSDate()
  }

  return typeof date === 'string' ? end.toISOString() : end
}

/**
 * Returns the start (from) and end (to) of a given period for a specified date.
 * Using function overload for extra type-safety
 * If the input date is a string, the function will return an object with the from and to keys formatted as strings.
 * If the input date is a Date object, the function will return an object with the from and to keys formatted as Date objects.
 *
 * @param {Date | string} value - The date for which to get the start and end of the period.
 * @param {LuxonDateTimeUnit} period - The period for which to get the start and end ('year', 'quarter', 'month', 'week', 'day', 'hour', 'minute').
 * @param {WeekDayISONumbers} [firstDayOfWeek] - Optional. The first day of the week (1-7, Monday-Sunday). Only used when period is 'week'.
 * @returns {Object} - An object containing the start and end of the period for the given date. The return type is an object with a from and to properties matching the type of the input date.
 */
export function getPeriod(
  value: string,
  period: LuxonDateTimeUnit,
  firstDayOfWeek?: WeekDayISONumbers,
): { from: string; to: string }
export function getPeriod(
  value: Date,
  period: LuxonDateTimeUnit,
  firstDayOfWeek?: WeekDayISONumbers,
): { from: Date; to: Date }
export function getPeriod(
  value: Date | string,
  period: LuxonDateTimeUnit,
  firstDayOfWeek?: WeekDayISONumbers,
): { from: Date | string; to: Date | string } {
  if (typeof value === 'string') {
    const from = getStartOfPeriod(value, period, firstDayOfWeek)
    const to = getEndOfPeriod(value, period, firstDayOfWeek)
    return { from, to }
  }

  const from = getStartOfPeriod(value, period, firstDayOfWeek)
  const to = getEndOfPeriod(value, period, firstDayOfWeek)
  return { from, to }
}

/**
 * Gets the current timezone UTC offset in minutes.
 * Positive values represent ahead of UTC, negative values represent behind UTC.
 *
 * @returns The timezone offset in minutes (e.g., 60 for UTC+1, -300 for UTC-5)
 *
 * @example
 * ```typescript
 * getTimezoneUtcOffset()
 * // If in UTC+2 timezone, returns: 120
 * // If in UTC-5 timezone, returns: -300
 * ```
 */
export function getTimezoneUtcOffset() {
  return getTodayDateTime().offset
}

/**
 * Converts a timestamp (milliseconds since Unix epoch) to an ISO 8601 date-time string.
 *
 * @param timestamp - The timestamp in milliseconds
 * @returns The ISO 8601 date-time string (e.g., "2024-01-15T10:30:00.000Z")
 *
 * @example
 * ```typescript
 * getDateTimeISOFromTimestamp(1705318200000)
 * // Returns: "2024-01-15T10:30:00.000Z"
 * ```
 */
export function getDateTimeISOFromTimestamp(timestamp: number) {
  return LuxonDateTime.fromMillis(timestamp).toISO() ?? ''
}
